import React, { useState, useEffect } from "react";
import { ref, onValue, set, off, get } from "firebase/database";
import  { database, firestore } from "../../../firebaseConfig";
import toast from "react-hot-toast";

import { doc, runTransaction, serverTimestamp } from "firebase/firestore";

/**
 * AddClientModal (client-side paybill reservation)
 *
 * Flow:
 * 1. Validate inputs and ensure username (OtherName) doesn't already exist in RTDB.
 * 2. Try up to MAX_ATTEMPTS to reserve a paybill doc at:
 *      companies/{companyName}/paybills/{paybillNumber}
 *    using a Firestore transaction (get + set). The transaction guarantees only one writer
 *    can create a given paybill doc.
 * 3. If reservation succeeds, write RTDB client nodes and increment counter.
 * 4. If reservation fails after attempts, abort and notify user.
 *
 * WARNING: Ensure Firestore rules allow authenticated users who create clients to write
 * to companies/{company}/paybills/*. If not allowed, the reservation transaction will fail.
 */

const MAX_PAYBILL_GEN_ATTEMPTS = 10;
const MAX_PREFIX_LENGTH = 3;
const COMPANY_SUFFIX_REGEX = /^(Ltd|Limited|Inc|Corp|Corporation|Company|Co)\.?$/i;

const AddClientModal = ({ open, onClose, companyName }) => {
  const db = database; // Realtime Database instance
  // Fields
  const [cloudName, setCloudName] = useState("");
  const [location, setLocation] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [clientType, setClientType] = useState("Direct"); // "Direct" or "Shop"
  const [clientId, setClientId] = useState("");
  const [unitPrice, setUnitPrice] = useState("");

  // UI state
  const [saving, setSaving] = useState(false);
  const [buttonCooldown, setButtonCooldown] = useState(false);

  // Fetch next client ID from Firebase counter
  useEffect(() => {
    if (!db) return undefined;
    const counterRef = ref(db, "A_Counter/Node/Client");
    onValue(counterRef, (snapshot) => {
      const id = snapshot.val();
      if (id) setClientId(id);
    });
    return () => {
      off(counterRef);
    };
  }, [db]);

  if (!open) return null;

  const generateUserName = (cloudName, id) => `${cloudName.replace(/\s+/g, "")}${id}`.toLowerCase();


  const rand5 = () => Math.floor(Math.random() * 100000).toString().padStart(5, "0");

  /**
   * Generate a company-specific paybill prefix from company name
   * Takes first letters of each word (up to 3 chars) and makes uppercase
   * Examples: "Dairy Corp" -> "DC$", "Neuracraft" -> "NEU$", "ABC Milk Ltd" -> "AML$"
   */
  const getCompanyPrefix = (companyName) => {
    if (!companyName || typeof companyName !== 'string') return 'NC';

    const cleaned = companyName.trim();
    if (!cleaned) return 'NC';

    // Split into words and filter out common suffixes
    const words = cleaned
      .split(/\s+/)
      .filter(w => w.length > 0)
      .filter(w => !COMPANY_SUFFIX_REGEX.test(w));

    if (words.length === 0) return 'NC';

    // If single word, take first 2-3 characters
    if (words.length === 1) {
      const word = words[0];
      return word.substring(0, Math.min(MAX_PREFIX_LENGTH, word.length)).toUpperCase();
    }

    // Multiple words: take first letter of each (up to 3 words)
    return words
      .slice(0, MAX_PREFIX_LENGTH)
      .map(w => w[0])
      .join('')
      .toUpperCase();
  };

  const generatePaybillCandidate = (companyName) => `${getCompanyPrefix(companyName)}${rand5()}`;

  const clearFields = () => {
    setCloudName("");
    setLocation("");
    setPhoneNumber("");
    setEmail("");
    setClientType("Direct");
    setUnitPrice("");
  };

  // Try to atomically reserve a paybill doc in Firestore
  const reservePaybillClientSide = async (companyId, cloudName, otherName) => {
    if (!companyId) throw new Error("Missing companyId");
    for (let attempt = 0; attempt < MAX_PAYBILL_GEN_ATTEMPTS; attempt += 1) {
      const candidate = generatePaybillCandidate(companyId);
      const docRef = doc(firestore, `/paybills/${candidate}`);
      try {
        // runTransaction ensures we check and set atomically
        const result = await runTransaction(firestore, async (t) => {
          const snap = await t.get(docRef);
          if (snap.exists()) {
            // candidate already taken -> abort transaction to try another candidate
            throw new Error("EXISTS");
          }
          const payload = {
            paybillNumber: candidate,
            companyName: companyId,
            cloudName: cloudName || null,
            otherName: otherName || null,
            balance: 0,
            createdAt: serverTimestamp(),
            meta: { createdFrom: "AddClientModal" },
          };
          t.set(docRef, payload);
          return { paybillNumber: candidate };
        });
        // reserved successfully
        return result.paybillNumber;
      } catch (err) {
        if (String(err.message || "").includes("EXISTS")) {
          // try another candidate
          continue;
        }
        // For permission or network errors, rethrow so caller can handle
        throw err;
      }
    }
    throw new Error("EXHAUSTED_ATTEMPTS");
  };

  const endCooldown = () => {
    setTimeout(() => setButtonCooldown(false), 1200);
  };

  const handleSave = async () => {
    if (saving || buttonCooldown) return; // guard
    setSaving(true);
    setButtonCooldown(true);

    try {
      if (!cloudName.trim()) {
        toast.error("Client name is required");
        setSaving(false);
        endCooldown();
        return;
      }
      if (!companyName) {
        toast.error("Company name not loaded yet");
        setSaving(false);
        endCooldown();
        return;
      }

      const userName = generateUserName(cloudName, clientId);
      const now = new Date().toISOString();

      // Quick RTDB existence check to avoid duplicate username
      try {
        const existingSnap = await get(ref(db, `Companies/${companyName}/ValidatedClients/${userName}`));
        if (existingSnap.exists()) {
          toast.error("A client with this username already exists. Please confirm and try again.");
          setSaving(false);
          return;
        }
      } catch (err) {
        console.warn("RTDB existence check failed, proceeding:", err);
        // proceed but be aware of possible duplicates if check truly failed
      }

      // Reserve paybill client-side using a Firestore transaction (atomic)
      let paybillNumber;
      try {
        paybillNumber = await reservePaybillClientSide(companyName, cloudName, userName);
      } catch (err) {
        console.error("Paybill reservation failed:", err);
        // Common failure causes:
        // - Firestore security rules prevent client creation of paybill docs (Permission denied)
        // - Network error
        // - Exhausted attempts (very unlikely)
        if (String(err.message || "").includes("permission") || err.code === "permission-denied") {
          toast.error("Permission denied reserving paybill. Please ask an admin to assign paybills.");
        } else if (String(err.message || "").includes("EXHAUSTED") || String(err.message || "").includes("EXHAUSTED_ATTEMPTS")) {
          toast.error("Could not generate a unique paybill. Please try again.");
        } else {
          toast.error("Failed to reserve paybill. Try again.");
        }
        setSaving(false);
        return;
      }

      // Build client RTDB data and set PaybillNumber
      const clientData = {
        Id: parseInt(clientId),
        CloudName: cloudName,
        OtherName: userName,
        Location: location,
        PhoneNumber: phoneNumber,
        Email: email,
        Status: "Active",
        CompanyName: companyName,
        CreatedAt: now,
        UpdatedAt: now,
        Mode: clientType === "Shop" ? "Distributor" : "Direct",
        LinkedShop: clientType === "Shop",
        Balance: 0,
        PaybillNumber: paybillNumber,
        UnitPrice: parseFloat(unitPrice) || 0, // 👈 unit price per liter for selling milk
      };

      // Prepare RTDB writes
      const updates = {};
      updates[`Companies/${companyName}/ValidatedClients/${userName}`] = { ...clientData };
      updates[`Companies/${companyName}/AIValidatedClients/${userName}`] = { ...clientData };
      updates[`NewValidatedClients/${userName}`] = { ...clientData };

      // Increment client counter
      const newCounter = (parseInt(clientId) || 0) + 1;
      updates[`A_Counter/Node/Client`] = newCounter.toString();

      // Apply RTDB writes
      await Promise.all(Object.entries(updates).map(([path, data]) => set(ref(db, path), data)));

      toast.success(`${cloudName} added — paybill ${paybillNumber}`);
      clearFields();
      onClose();
    } catch (err) {
      console.error("Error in AddClientModal.handleSave:", err);
      toast.error("Error adding client. Try again.");
    } finally {
      setSaving(false);
      endCooldown();
    }
  };

  const backdropOnClick = (e) => {
    if (saving || buttonCooldown) {
      e.stopPropagation();
      return;
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50"
      onClick={backdropOnClick}
      style={{ cursor: saving || buttonCooldown ? "not-allowed" : "default" }}
    >
      <div
        className="bg-white rounded-xl p-6 shadow-xl w-96 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-cyan-700 mb-4">Add New Client</h2>

        <div className="space-y-3">
          <input
            type="text"
            placeholder="Client Name"
            value={cloudName}
            onChange={(e) => setCloudName(e.target.value)}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
            disabled={saving}
          />
          <input
            type="text"
            placeholder="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
            disabled={saving}
          />
          <input
            type="text"
            placeholder="Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
            disabled={saving}
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
            disabled={saving}
          />

          <input
            type="number"
            placeholder="Unit Price per Liter (KES)"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            step="0.01"
            min="0"
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
            disabled={saving}
          />
          <select
            value={clientType}
            onChange={(e) => setClientType(e.target.value)}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
            disabled={saving}
          >
            <option value="Direct">Direct Client</option>
            <option value="Shop">Shop / Distributor</option>
          </select>

          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={() => !saving && onClose()}
              className="px-4 py-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
              disabled={saving}
            >
              Cancel
            </button>
            {buttonCooldown ? (
              <div className="px-4 py-2 rounded bg-gray-200 text-gray-500 text-sm">
                Finalizing...
              </div>
            ) : (
              <button
                onClick={handleSave}
                className={`px-4 py-2 rounded text-white transition ${saving ? "bg-cyan-300 cursor-not-allowed" : "bg-cyan-600 hover:bg-cyan-700"}`}
                disabled={saving}
              >
                {saving ? "Adding…" : "Add Client"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddClientModal;
